import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../../../redux/hooks';
import { CONVERSATION_THREAD_READ } from '../../../utils/apollo/conversations';
import {
  IConversationThreadFieldsFragment,
  IConversationThreadReadMutation,
  IConversationThreadReadMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { updateThread } from '../../../redux/slices/conversationThreads';

const useReadConversationThreadEffect = (
  conversationThread: IConversationThreadFieldsFragment,
) => {
  const dispatch = useAppDispatch();

  const handleMutationComplete = ({
    conversationThreadRead,
  }: IConversationThreadReadMutation) => {
    if (printGraphqlErrors(conversationThreadRead.errors)) {
      return;
    }

    const thread = conversationThreadRead.thread;
    assertNotNil(thread);
    dispatch(updateThread(thread));
  };

  const [readConversationThreadMutation] = useMutation<
    IConversationThreadReadMutation,
    IConversationThreadReadMutationVariables
  >(CONVERSATION_THREAD_READ, {
    onCompleted: handleMutationComplete,
  });

  // When we have some unread comments, run readConversationThread on mount
  useEffect(() => {
    if (conversationThread.hasUnreadComments) {
      assertCondition(conversationThread.comments.length > 0);
      // provide latest comment id
      const commentId =
        conversationThread.comments[conversationThread.comments.length - 1].id;

      readConversationThreadMutation({
        variables: {
          input: {
            threadId: conversationThread.id,
            commentId,
          },
        },
      });
    }
  }, [conversationThread, readConversationThreadMutation]);
};

export default useReadConversationThreadEffect;
